import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `class LRUCache<K, V> {
  constructor(private capacity: number) {}

  get(key: K): V | undefined {
    // TODO
    return undefined;
  }

  put(key: K, value: V): void {
    // TODO
  }

  get size(): number {
    // TODO
    return 0;
  }
}
`;

const solution = `class LRUCache<K, V> {
  // A Map is insertion-ordered and its iterator yields oldest first, so it is a
  // hash map and a queue at once. Deleting and re-setting a key moves it to the
  // end, which is exactly "mark as recently used", and keeps every operation
  // O(1). Doing this with a plain object plus an array of keys makes "mark as
  // used" an O(n) splice.
  private entries = new Map<K, V>();

  constructor(private capacity: number) {
    if (capacity < 1) throw new Error("capacity must be at least 1");
  }

  get(key: K): V | undefined {
    if (!this.entries.has(key)) return undefined;
    const value = this.entries.get(key) as V;
    // A read counts as a use. Without this, a hot key that is never written
    // again is evicted while a cold one that was written recently survives.
    this.entries.delete(key);
    this.entries.set(key, value);
    return value;
  }

  put(key: K, value: V): void {
    // Delete first so an overwrite refreshes recency AND does not grow size.
    if (this.entries.has(key)) this.entries.delete(key);
    this.entries.set(key, value);
    if (this.entries.size > this.capacity) {
      // The first key in iteration order is the least recently used.
      const oldest = this.entries.keys().next().value as K;
      this.entries.delete(oldest);
    }
  }

  get size(): number {
    return this.entries.size;
  }
}`;

const harness = HARNESS_KIT + `
if (typeof LRUCache !== "function") {
  throw new Error("LRUCache is not defined. Keep the class name as given.");
}

const __tests = [
  {
    name: "stores and returns a value",
    about: "The basic contract before eviction is involved.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1);
      __expectExact("put('a',1); get('a')", function () { return c.get("a"); }, 1);
    }
  },
  {
    name: "a missing key is undefined",
    about: "A miss is undefined, not null and not a throw.",
    run: function () {
      const c = new LRUCache(2);
      __expectExact("get('nope') on an empty cache", function () { return c.get("nope"); }, undefined);
    }
  },
  {
    name: "evicts the least recently used once over capacity",
    about: "The whole point: past capacity, something has to go, and it is the coldest entry.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1);
      c.put("b", 2);
      c.put("c", 3);
      __expectExact("capacity 2; put a,b,c; get('a')", function () { return c.get("a"); }, undefined);
      __expectExact("... get('b')", function () { return c.get("b"); }, 2);
      __expectExact("... get('c')", function () { return c.get("c"); }, 3);
    }
  },
  {
    name: "size never exceeds capacity",
    about: "Eviction must happen on the write that overflows, not lazily later.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1); c.put("b", 2); c.put("c", 3); c.put("d", 4);
      __expectExact("capacity 2 after 4 puts, size", function () { return c.size; }, 2);
    }
  },
  {
    name: "a read counts as a use",
    hidden: true,
    about: "This is what makes it an LRU rather than a first-in-first-out queue. Miss it and a key read constantly is evicted while a key written once and never touched survives.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1);
      c.put("b", 2);
      c.get("a");        // 'a' is now the most recently used
      c.put("c", 3);     // so 'b' should be evicted, not 'a'
      __expectExact("put a,b; get a; put c; get('a')", function () { return c.get("a"); }, 1);
      __expectExact("... get('b')", function () { return c.get("b"); }, undefined);
    }
  },
  {
    name: "overwriting an existing key does not grow the cache",
    hidden: true,
    about: "An overwrite is not an insert. Treating it as one evicts a perfectly good entry to make room for a key that was already there.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1);
      c.put("b", 2);
      c.put("a", 99);
      __expectExact("put a,b, then overwrite a; size", function () { return c.size; }, 2);
      __expectExact("... get('b')", function () { return c.get("b"); }, 2);
      __expectExact("... get('a')", function () { return c.get("a"); }, 99);
    }
  },
  {
    name: "overwriting an existing key refreshes it",
    hidden: true,
    about: "Writing to a key is a use of it, the same as reading.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1);
      c.put("b", 2);
      c.put("a", 10);   // 'a' is now newest
      c.put("c", 3);    // so 'b' goes
      __expectExact("overwrite a then put c; get('b')", function () { return c.get("b"); }, undefined);
      __expectExact("... get('a')", function () { return c.get("a"); }, 10);
    }
  },
  {
    name: "a miss does not count as a use",
    hidden: true,
    about: "Reading a key that is not there must not disturb the ordering, or a stream of misses reshuffles the cache.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", 1);
      c.put("b", 2);
      c.get("zzz");
      c.put("c", 3);
      __expectExact("get a missing key then put c; get('a')", function () { return c.get("a"); }, undefined);
      __expectExact("... get('b')", function () { return c.get("b"); }, 2);
    }
  },
  {
    name: "capacity of one holds exactly the last key",
    hidden: true,
    about: "The degenerate case, where an off-by-one in the eviction check shows up immediately.",
    run: function () {
      const c = new LRUCache(1);
      c.put("a", 1);
      c.put("b", 2);
      __expectExact("capacity 1; put a,b; get('a')", function () { return c.get("a"); }, undefined);
      __expectExact("... get('b')", function () { return c.get("b"); }, 2);
      __expectExact("... size", function () { return c.size; }, 1);
    }
  },
  {
    name: "storing undefined is distinguishable from a miss",
    hidden: true,
    about: "If has() is implemented as get() !== undefined, a stored undefined reads as absent and is re-fetched forever. Caches exist to avoid exactly that.",
    run: function () {
      const c = new LRUCache(2);
      c.put("a", undefined);
      c.put("b", 2);
      __expectExact("put('a', undefined); size", function () { return c.size; }, 2);
    }
  },
  {
    name: "eviction order survives a long mixed sequence",
    hidden: true,
    about: "Individually correct operations can still compose wrongly. This is the sequence that catches a recency list updated in the wrong place.",
    run: function () {
      const c = new LRUCache(3);
      c.put("a", 1); c.put("b", 2); c.put("c", 3);
      c.get("a");            // order: b, c, a
      c.put("d", 4);         // evicts b
      c.get("c");            // order: a, d, c
      c.put("e", 5);         // evicts a
      __expectExact("mixed sequence; get('b')", function () { return c.get("b"); }, undefined);
      __expectExact("... get('a')", function () { return c.get("a"); }, undefined);
      __expectExact("... get('c')", function () { return c.get("c"); }, 3);
      __expectExact("... get('d')", function () { return c.get("d"); }, 4);
      __expectExact("... get('e')", function () { return c.get("e"); }, 5);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Two questions decide most of this. What should a get do to recency, and what should a put on a key that is already there do to the size? A Map answers both, because it is insertion-ordered and its iterator yields oldest first.",
    code: `class LRUCache<K, V> {
  // A hash map and a queue at once. Delete-then-set moves a key to the end,
  // which is exactly "mark as recently used", and every operation stays O(1).
  // A plain object plus an array of keys makes "mark as used" an O(n) splice.
  private entries = new Map<K, V>();

  constructor(private capacity: number) {
    if (capacity < 1) throw new Error("capacity must be at least 1");
  }

  get size(): number {
    return this.entries.size;
  }
}`,
  },
  {
    label: "Approach",
    body: "get re-inserts on a hit, which is what makes it least-recently-used rather than first-in-first-out. put deletes before setting, so an overwrite refreshes recency and does not grow the size. The eviction candidate is the first key the Map yields.",
    code: `get(key: K): V | undefined {
  if (!this.entries.has(key)) return undefined;   // has(), so a stored undefined is not a miss
  const value = this.entries.get(key) as V;
  this.entries.delete(key);                       // a read counts as a use
  this.entries.set(key, value);
  return value;
}

put(key: K, value: V): void {
  if (this.entries.has(key)) this.entries.delete(key);   // overwrite refreshes, does not grow
  this.entries.set(key, value);
  if (this.entries.size > this.capacity) {
    const oldest = this.entries.keys().next().value as K;
    this.entries.delete(oldest);
  }
}`,
  },
  {
    label: "The trap",
    body: "Reading has to count as a use, or you have built a first-in-first-out queue that evicts the key you read on every request. Overwriting an existing key is not an insert: it must not grow the size and must not evict anything. And a miss must not disturb the ordering, or a stream of misses reshuffles the cache.",
  },
];

export const lruCache: Drill = {
  id: "lru-cache",
  track: "typescript",
  title: "LRU Cache",
  pattern: "lru-cache",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `You are putting a cache in front of an expensive lookup. It has a fixed capacity, and when it is full the least recently used entry is thrown out.

\`\`\`ts
class LRUCache<K, V> {
  constructor(capacity: number);
  get(key: K): V | undefined;
  put(key: K, value: V): void;
  get size(): number;
}
\`\`\`

## Requirements

- **\`get\` returns the value or \`undefined\`**, and **counts as a use**.
- **\`put\` inserts or overwrites**, and also counts as a use.
- **Size never exceeds capacity.** The write that overflows evicts the least recently used entry.
- **\`get\` and \`put\` should both be O(1).** Expect to be asked to justify it.

## Example

\`\`\`ts
const cache = new LRUCache<string, number>(2);
cache.put("a", 1);
cache.put("b", 2);
cache.get("a");      // 1  → "a" is now the most recently used
cache.put("c", 3);   // over capacity → evicts "b", the coldest
cache.get("b");      // undefined
cache.get("a");      // 1
\`\`\`

## Where this gets interesting

Two things separate a real LRU from something that merely evicts.

The first is that **reading counts**. If only writes refresh recency you have built a first-in-first-out queue with extra steps, and the key your application reads on every request is the one you throw away.

The second is **overwriting an existing key**. It is not an insert, so it must not grow the size, and it must not trigger an eviction to make room for a key that was already there.

Then there is the complexity requirement. A key list you \`splice\` on every access is O(n), and works perfectly on ten items.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(1) for get, put and size. A Map is insertion-ordered with an oldest-first iterator, so delete-then-set is an O(1) 'move to most recent' and the first key is always the eviction candidate.",
};
